import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Cluster, Stack } from "@dropbox/dig-foundations";
import classNames from "classnames";
import * as React from "react";
import styles from "./ChatShowcase.module.css";

export interface ChatShowcaseProps {
  /**
   * The text to display in the input field.
   */
  inputText: string;

  /**
   * The number of dots to render for the AI response.
   */
  numDots?: number;

  /**
   * The number of dots to render for the AI response.
   */
  maxLines?: number;

  /**
   * Whether to animate the dot elements
   */
  animate?: boolean;
}

/**
 * ChatShowcase displays a simple chat conversation between a user and an AI assistant.
 */
export const ChatShowcase: React.FC<ChatShowcaseProps> = ({
  inputText,
  numDots = 3,
  maxLines = 2,
  animate = true,
}) => {
  /**
   * This component implements an initial version of a chat message component.
   * Create a dedicated chat message component once design is aligned with chat.
   */

  return (
    <Stack gap="20" width="100%">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        width="100%"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          backgroundColor="Opacity Surface"
          borderRadius="XLarge"
          paddingTop="8"
          paddingBottom="8"
          paddingLeft="16"
          paddingRight="16"
        >
          <Text color="subtle">
            <Truncate lines={maxLines}>{inputText}</Truncate>
          </Text>
        </Box>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        width="100%"
        paddingTop="12"
        paddingBottom="12"
      >
        <Cluster gap="3px">
          {Array.from({ length: numDots }).map((_, i) => (
            <Cluster.Item key={i}>
              <Box
                borderRadius="Circular"
                className={classNames(styles.dot, {
                  [styles.dotAnimated]: animate,
                })}
                data-testid="chat-dot"
                style={{
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            </Cluster.Item>
          ))}
        </Cluster>
      </Box>
    </Stack>
  );
};
